import { useEffect, useRef, useState } from "react";
import { getStoredHostSession } from "./webAuth";
import { useFeatureFlagEnabled } from "@posthog/react";
// Same query the controller uses everywhere else for "mobile".
import { MOBILE_QUERY } from "./layout";

// Featurebase live-chat (Messenger) integration.
//
// Signed-in accounts boot with a server-signed JWT; guests stay anonymous.
// Mounted only on controller pages, with shutdown on exit or identity change.
// The Featurebase signing secret lives exclusively in the account service.
//
// Launcher visibility is responsive: on desktop we show Featurebase's native
// floating bubble (bottom-right); on mobile (<768px, matching the app's
// breakpoint) we hide it and let the in-app hamburger menu open the chat.
//
// KILL SWITCH: the whole widget (SDK load, launcher, and menu entries) is gated
// behind the PostHog feature flag `featurebase-live-chat`, fail-closed — it
// only mounts when the flag resolves `true`. Disabling the flag in PostHog
// remotely removes the widget everywhere, including localhost (index.tsx keeps
// feature flags loading in local dev). Create the flag at 100% rollout or chat
// will not appear.

const FLAG_KEY = "featurebase-live-chat";

const FEATUREBASE_APP_ID = "6a557282fb7db84a371a4666";
const SDK_ID = "featurebase-sdk";
const SDK_SRC = "https://do.featurebase.app/js/sdk.js";
const MOBILE_STYLE_ID = "featurebase-mobile-suppress";

// On mobile we want chat to be strictly menu-only: nothing floating over the
// clicker. Hiding the launcher (hideDefaultLauncher) stops the bubble, but
// Featurebase still docks its *inbound-message notification preview* (and the
// announcement banner) at the launcher's bottom-right anchor — which lands on
// top of the clicker's controls on a phone. Suppress those floating frames
// under the mobile breakpoint; the full-screen messenger the menu opens
// (`featurebase-messenger-popup-wrapper`) is untouched, so opening still works.
const MOBILE_SUPPRESS_CSS = `@media ${MOBILE_QUERY} {
  #featurebase-messenger-launcher-frame-wrapper,
  .featurebase-messenger-launcher-frame-wrapper,
  #featurebase-notification-wrapper,
  .featurebase-notification-frame-wrapper,
  #featurebase-banner-frame-wrapper,
  .featurebase-banner-frame-wrapper {
    display: none !important;
  }
}`;

type FeaturebaseFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    Featurebase?: FeaturebaseFn;
  }
}

// Inject the Featurebase queue stub + SDK loader (idempotent). Calls made
// before the real SDK finishes loading are queued on `Featurebase.q` and
// replayed once it initialises.
function ensureSdkLoaded() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  if (typeof window.Featurebase !== "function") {
    const stub: FeaturebaseFn = function (...args: unknown[]) {
      (stub.q = stub.q || []).push(args);
    } as FeaturebaseFn;
    window.Featurebase = stub;
  }

  if (!document.getElementById(MOBILE_STYLE_ID)) {
    const style = document.createElement("style");
    style.id = MOBILE_STYLE_ID;
    style.textContent = MOBILE_SUPPRESS_CSS;
    document.head.appendChild(style);
  }

  if (!document.getElementById(SDK_ID)) {
    const script = document.createElement("script");
    script.id = SDK_ID;
    script.src = SDK_SRC;
    const first = document.getElementsByTagName("script")[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      document.head.appendChild(script);
    }
  }
}

// Set once the account API says identity is not configured (503). It will not
// become configured without a deploy, so asking again — on every remount and
// every 45-minute refresh — only adds a 5xx and a round trip per controller.
// Module scope, so a page reload (e.g. after that deploy) tries again.
let identityNotConfigured = false;

/** Test-only: forget a previous "not configured" answer. */
export function resetFeaturebaseIdentityForTests() {
  identityNotConfigured = false;
}

function fb(...args: unknown[]) {
  window.Featurebase?.(...args);
}

// Boots the Messenger for the lifetime of the mounting component (while the
// kill-switch flag is on). Keeps the launcher's responsive visibility and the
// chat theme in sync with the app. Returns `enabled` so callers can hide their
// own chat menu entries when the widget is killed, plus a gated `openMessenger`.
export function useFeaturebaseMessenger({
  theme,
  disabled = false,
}: {
  theme: "light" | "dark";
  // Never boot, whatever the flag says. A phone paired to one deck from the
  // host controller's QR has no account and no support chat (COP-17).
  disabled?: boolean;
}) {
  const flagEnabled = useFeatureFlagEnabled(FLAG_KEY);
  // Fail-closed everywhere: only mount when the flag resolves true (undefined
  // while flags load ⇒ stays off). localhost loads flags too (see index.tsx),
  // so disabling the flag in PostHog kills the widget in local dev as well.
  const enabled = flagEnabled === true && !disabled;
  const [sessionToken, setSessionToken] = useState(() => getStoredHostSession()?.sessionToken ?? null);
  const [readyFor, setReadyFor] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    const syncSession = () => setSessionToken(getStoredHostSession()?.sessionToken ?? null);
    window.addEventListener("storage", syncSession);
    window.addEventListener("copresent-session-changed", syncSession);
    window.addEventListener("focus", syncSession);
    return () => {
      window.removeEventListener("storage", syncSession);
      window.removeEventListener("copresent-session-changed", syncSession);
      window.removeEventListener("focus", syncSession);
    };
  }, []);

  // The breakpoint listener re-boots on resize/rotate; read the latest theme
  // through a ref so a re-boot never resurrects a stale value.
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    if (!enabled) return;
    ensureSdkLoaded();

    const abort = new AbortController();
    let featurebaseJwt: string | undefined;
    let ready = false;
    setReadyFor(undefined);
    const boot = (isMobile: boolean) => {
      if (!ready) return;
      fb("boot", {
        appId: FEATUREBASE_APP_ID,
        theme: themeRef.current,
        ...(featurebaseJwt ? { featurebaseJwt } : {}),
        // Native bubble on desktop; hidden on mobile (opened from the menu).
        hideDefaultLauncher: isMobile,
      });
    };

    const mql = window.matchMedia(MOBILE_QUERY);
    const identify = async () => {
      try {
        if (sessionToken && !identityNotConfigured) {
          const apiUrl = process.env.REACT_APP_QA_API_URL;
          if (!apiUrl) throw new Error("Account API is not configured");
          const response = await fetch(`${apiUrl}/account/featurebase-token`, {
            headers: { Authorization: `Bearer ${sessionToken}` }, signal: abort.signal,
          });
          if (response.status === 503) identityNotConfigured = true;
          if (!response.ok) throw new Error("Featurebase identity unavailable");
          const body = await response.json();
          if (typeof body.featurebaseJwt !== "string" || !body.featurebaseJwt) {
            throw new Error("Featurebase identity missing");
          }
          if (abort.signal.aborted) return;
          featurebaseJwt = body.featurebaseJwt;
        }
        if (abort.signal.aborted) return;
        ready = true;
        boot(mql.matches);
        setReadyFor(sessionToken);
      } catch {
        // Keep existing anonymous support available during backend rollout or
        // an outage, without ever sending unverified identity fields.
        if (abort.signal.aborted) return;
        featurebaseJwt = undefined;
        fb("shutdown");
        ready = true;
        boot(mql.matches);
        setReadyFor(sessionToken);
        console.warn("Featurebase identity unavailable; using guest chat.");
      }
    };
    void identify();
    // Renew before the one-hour JWT expires, including long presentations.
    // Not when identity is unconfigured: there is no JWT to renew.
    const refresh = window.setInterval(() => {
      if (!identityNotConfigured) void identify();
    }, 45 * 60 * 1000);

    // Re-booting is Featurebase's documented way to update boot options; this
    // only fires when the viewport actually crosses the mobile breakpoint.
    const onChange = (e: MediaQueryListEvent) => boot(e.matches);
    mql.addEventListener("change", onChange);

    return () => {
      abort.abort();
      window.clearInterval(refresh);
      mql.removeEventListener("change", onChange);
      // Remove the widget when leaving the controller (or when the kill switch
      // flips off at runtime) so it never lingers outside the controller.
      fb("shutdown");
    };
    // Reset the widget when the account changes; theme is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionToken]);

  // Keep the chat's theme aligned with the app's light/dark toggle.
  useEffect(() => {
    if (enabled) fb("setTheme", theme);
  }, [theme, enabled]);

  // Open the live chat on the "messages" view. No-op while killed.
  const openMessenger = () => {
    if (enabled && readyFor === sessionToken) fb("show", "messages");
  };

  return { openMessenger, enabled: enabled && readyFor === sessionToken };
}
